// TelcoTrack — týmové poznámky u zařízení (parita Úřady / obligation notes)

export interface EquipmentNoteEntry {
  id: string;
  text: string;
  createdAt: string;
  authorEmail: string;
  authorName: string | null;
}

const MAX_NOTE_LENGTH = 4000;

function randomEquipmentNoteId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `${Date.now()}-${Math.random().toString(36).slice(2, 11)}`;
}

function asTrimmedString(value: unknown): string {
  return typeof value === 'string' ? value.trim() : '';
}

function asIso(value: unknown, fallback: string): string {
  if (typeof value === 'string' && value.trim()) {
    const d = new Date(value);
    if (!Number.isNaN(d.getTime())) return d.toISOString();
  }
  return fallback;
}

export function parseEquipmentNotesEntries(raw: unknown): EquipmentNoteEntry[] {
  if (!Array.isArray(raw)) return [];
  const out: EquipmentNoteEntry[] = [];
  for (const item of raw) {
    if (!item || typeof item !== 'object' || Array.isArray(item)) continue;
    const o = item as Record<string, unknown>;
    const text = asTrimmedString(o['text']);
    if (!text) continue;
    const id = asTrimmedString(o['id']) || randomEquipmentNoteId();
    out.push({
      id,
      text,
      createdAt: asIso(o['createdAt'] ?? o['created_at'], new Date(0).toISOString()),
      authorEmail: asTrimmedString(o['authorEmail'] ?? o['author_email']).toLowerCase(),
      authorName: asTrimmedString(o['authorName'] ?? o['author_name']) || null,
    });
  }
  return out;
}

/** Legacy sloupec `equipment.notes` → jedna položka historie. */
export function coalesceEquipmentNotesForDisplay(
  entriesRaw: unknown,
  legacyNotes: string | null | undefined,
  fallbackCreatedAt: string,
): EquipmentNoteEntry[] {
  const entries = parseEquipmentNotesEntries(entriesRaw);
  if (entries.length > 0) {
    return [...entries].sort((a, b) => b.createdAt.localeCompare(a.createdAt));
  }
  const legacy = (legacyNotes ?? '').trim();
  if (!legacy) return [];
  return [
    {
      id: 'legacy-notes',
      text: legacy,
      createdAt: asIso(fallbackCreatedAt, new Date(0).toISOString()),
      authorEmail: '',
      authorName: null,
    },
  ];
}

export function buildEquipmentNoteEntry(input: {
  text: string;
  authorEmail: string | null;
  authorName?: string | null;
  createdAt?: string;
}): EquipmentNoteEntry {
  const text = input.text.trim().slice(0, MAX_NOTE_LENGTH);
  if (!text) {
    throw new Error('Poznámka nesmí být prázdná.');
  }
  return {
    id: randomEquipmentNoteId(),
    text,
    createdAt: input.createdAt ?? new Date().toISOString(),
    authorEmail: (input.authorEmail ?? '').trim().toLowerCase(),
    authorName: (input.authorName ?? '').trim() || null,
  };
}

export function appendEquipmentNoteEntry(
  entriesRaw: unknown,
  legacyNotes: string | null | undefined,
  fallbackCreatedAt: string,
  next: EquipmentNoteEntry,
): EquipmentNoteEntry[] {
  let entries = parseEquipmentNotesEntries(entriesRaw);
  if (entries.length === 0) {
    const legacy = (legacyNotes ?? '').trim();
    if (legacy) {
      entries = [
        {
          id: randomEquipmentNoteId(),
          text: legacy,
          createdAt: asIso(fallbackCreatedAt, new Date(0).toISOString()),
          authorEmail: '',
          authorName: null,
        },
      ];
    }
  }
  return [...entries, next];
}

export function mutableEquipmentNoteEntries(
  entriesRaw: unknown,
  legacyNotes: string | null | undefined,
  fallbackCreatedAt: string,
): EquipmentNoteEntry[] {
  return coalesceEquipmentNotesForDisplay(entriesRaw, legacyNotes, fallbackCreatedAt);
}

export function canModifyEquipmentNote(
  entry: EquipmentNoteEntry,
  caller: { email: string | null; isAdmin: boolean },
): boolean {
  if (caller.isAdmin) return true;
  const callerEmail = (caller.email ?? '').trim().toLowerCase();
  const authorEmail = (entry.authorEmail ?? '').trim().toLowerCase();
  if (callerEmail && authorEmail && callerEmail === authorEmail) return true;
  return false;
}

export function updateEquipmentNoteEntry(
  entriesRaw: unknown,
  legacyNotes: string | null | undefined,
  fallbackCreatedAt: string,
  noteId: string,
  nextText: string,
): EquipmentNoteEntry[] {
  const text = nextText.trim().slice(0, MAX_NOTE_LENGTH);
  if (!text) {
    throw new Error('Poznámka nesmí být prázdná.');
  }
  const entries = mutableEquipmentNoteEntries(entriesRaw, legacyNotes, fallbackCreatedAt);
  const idx = entries.findIndex((n) => n.id === noteId.trim());
  if (idx < 0) {
    throw new Error('Poznámka nenalezena.');
  }
  const next = [...entries];
  next[idx] = { ...next[idx], text };
  return next;
}

export function deleteEquipmentNoteEntry(
  entriesRaw: unknown,
  legacyNotes: string | null | undefined,
  fallbackCreatedAt: string,
  noteId: string,
): EquipmentNoteEntry[] {
  const entries = mutableEquipmentNoteEntries(entriesRaw, legacyNotes, fallbackCreatedAt);
  const id = noteId.trim();
  if (!entries.some((n) => n.id === id)) {
    throw new Error('Poznámka nenalezena.');
  }
  return entries.filter((n) => n.id !== id);
}

export const EQUIPMENT_NOTE_LEGACY_ID = 'legacy-notes';
export const EQUIPMENT_NOTE_MAX_LENGTH = MAX_NOTE_LENGTH;
